import axios from 'axios';
import { 
  Event, 
  User, 
  Guardrail, 
  GuardrailLibrary,
  BehavioralPrompt, 
  DocumentInfo, 
  Session, 
  ChatRequest, 
  ChatResponse,
  ApiResponse,
  UserStats,
  UserStatsUsersResponse,
  SessionStats,
  EventStats,
  Notification,
  NotificationReceipt,
  AppUser,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  ChangePasswordRequest,
  CreateCompanyUserRequest,
  UpdateAppUserRequest,
  ResetPasswordRequest,
  UserStatsResponse,
  SignupFormField,
  AITraceItem,
  AITraceDetailResponse,
} from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5004/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      
      // Don't redirect if we're on an event landing page - let it handle its own auth flow
      const isEventPage = currentPath.startsWith('/event/');
      
      // Clear auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Redirect to login only for admin routes, not event pages
      if (!isEventPage && currentPath !== '/login') {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Events API
export const eventsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; isActive?: boolean; status?: 'active' | 'inactive' | 'inprogress'; sortBy?: string; sortOrder?: 'asc' | 'desc'; clientId?: string }) =>
    api.get<ApiResponse<Event[]>>('/events', { params }),
  
  getById: (id: string) =>
    api.get<ApiResponse<Event>>(`/events/${id}`),

  getSignupForm: (eventId: string) =>
    api.get<ApiResponse<{ signupFormFields: SignupFormField[] }>>(`/events/${eventId}/signup-form`),
  
  create: (data: Omit<Event, '_id' | 'createdAt' | 'updatedAt'>) =>
    api.post<ApiResponse<Event>>('/events', data),
  
  update: (id: string, data: Partial<Event>) =>
    api.put<ApiResponse<Event>>(`/events/${id}`, data),
  
  delete: (id: string) =>
    api.delete<ApiResponse<void>>(`/events/${id}`),
  
  getStats: (id: string) =>
    api.get<ApiResponse<EventStats>>(`/events/${id}/stats`),

  uploadImage: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ApiResponse<Event>>(`/events/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export interface EmailOtpRecord {
  _id: string;
  eventId: string;
  clientId?: string;
  userObjectId?: string;
  emailLower: string;
  status: 'created' | 'sent' | 'failed' | 'verified' | 'expired' | 'invalidated';
  expiresAt: string;
  verifiedAt?: string;
  attemptCount: number;
  lastAttemptAt?: string;
  resendCount: number;
  lastSentAt?: string;
  provider: 'ses' | 'acs-email' | 'smtp' | 'none';
  providerMessageId?: string;
  providerStatus?: string;
  lastErrorCode?: string;
  lastErrorMessage?: string;
  requestIp?: string;
  userAgent?: string;
  createdAt: string;
  updatedAt: string;
}

export const emailOtpAdminApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    eventId?: string;
    clientId?: string;
    status?: string;
    email?: string;
    ip?: string;
    from?: string;
    to?: string;
  }) => api.get<ApiResponse<EmailOtpRecord[]>>('/admin/email-otp', { params }),

  getById: (id: string) => api.get<ApiResponse<EmailOtpRecord>>(`/admin/email-otp/${id}`),

  resend: (id: string) => api.post<ApiResponse<any>>(`/admin/email-otp/${id}/resend`),

  invalidate: (id: string) => api.post<ApiResponse<any>>(`/admin/email-otp/${id}/invalidate`),
};

// Users API
export const usersApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; language?: string; clientId?: string; eventId?: string }) =>
    api.get<ApiResponse<User[]>>('/users', { params }),
  
  getById: (userId: string) =>
    api.get<ApiResponse<User>>(`/users/${userId}`),
  
  update: (userId: string, data: Partial<User>) =>
    api.put<ApiResponse<User>>(`/users/${userId}`, data),
  
  delete: (userId: string) =>
    api.delete<ApiResponse<void>>(`/users/${userId}`),
  
  getStats: (userId: string) =>
    api.get<ApiResponse<UserStats>>(`/users/${userId}/stats`),

  getGeneralStatistics: (params?: { clientId?: string; eventId?: string; startDate?: string; endDate?: string }) =>
    api.get<ApiResponse<UserStats>>('/users/statistics', { params }),

  getStatisticsUsers: (params?: {
    clientId?: string;
    eventId?: string;
    page?: number;
    limit?: number;
    search?: string;
    status?: 'active' | 'inactive' | 'never_chatted';
    verified?: 'verified' | 'pending' | 'unverified';
    language?: string;
    occupation?: string;
    businessNature?: string;
  }) =>
    api.get<ApiResponse<UserStatsUsersResponse>>('/users/statistics/users', { params }),
  
  getSessions: (userId: string, params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<Session[]>>(`/users/${userId}/sessions`, { params }),
};

// Guardrails API
export const guardrailsApi = {
  getAll: (params?: { page?: number; limit?: number; eventId?: string; isActive?: boolean }) =>
    api.get<ApiResponse<Guardrail[]>>('/guardrails', { params }),
  
  getById: (id: string) =>
    api.get<ApiResponse<Guardrail>>(`/guardrails/${id}`),
  
  create: (data: Omit<Guardrail, '_id' | 'createdAt' | 'updatedAt'>) =>
    api.post<ApiResponse<Guardrail>>('/guardrails', data),
  
  update: (id: string, data: Partial<Guardrail>) =>
    api.put<ApiResponse<Guardrail>>(`/guardrails/${id}`, data),
  
  delete: (id: string) =>
    api.delete<ApiResponse<void>>(`/guardrails/${id}`),
  
  getByEvent: (eventId: string, params?: { isActive?: boolean }) =>
    api.get<ApiResponse<Guardrail[]>>(`/guardrails/event/${eventId}`, { params }),
};

// Guardrail Library API
export const guardrailLibraryApi = {
  getAll: (params?: { page?: number; limit?: number; category?: string; tags?: string; isPublic?: boolean; search?: string }) =>
    api.get<ApiResponse<GuardrailLibrary[]>>('/guardrail-library', { params }),
  
  getById: (id: string) =>
    api.get<ApiResponse<GuardrailLibrary>>(`/guardrail-library/${id}`),
  
  create: (data: Omit<GuardrailLibrary, '_id' | 'createdAt' | 'updatedAt' | 'usageCount'>) =>
    api.post<ApiResponse<GuardrailLibrary>>('/guardrail-library', data),
  
  update: (id: string, data: Partial<GuardrailLibrary>) =>
    api.put<ApiResponse<GuardrailLibrary>>(`/guardrail-library/${id}`, data),
  
  delete: (id: string) =>
    api.delete<ApiResponse<void>>(`/guardrail-library/${id}`),
  
  incrementUsage: (id: string) =>
    api.post<ApiResponse<GuardrailLibrary>>(`/guardrail-library/${id}/use`),
};

// Behavioral Prompts API
export const behavioralPromptsApi = {
  getAll: (params?: { page?: number; limit?: number; eventId?: string; language?: string; isActive?: boolean }) =>
    api.get<ApiResponse<BehavioralPrompt[]>>('/behavioral-prompts', { params }),
  
  getById: (id: string) =>
    api.get<ApiResponse<BehavioralPrompt>>(`/behavioral-prompts/${id}`),
  
  create: (data: Omit<BehavioralPrompt, '_id' | 'createdAt' | 'updatedAt'>) =>
    api.post<ApiResponse<BehavioralPrompt>>('/behavioral-prompts', data),
  
  update: (id: string, data: Partial<BehavioralPrompt>) =>
    api.put<ApiResponse<BehavioralPrompt>>(`/behavioral-prompts/${id}`, data),
  
  delete: (id: string) =>
    api.delete<ApiResponse<void>>(`/behavioral-prompts/${id}`),
  
  getByEvent: (eventId: string, params?: { language?: string; isActive?: boolean }) =>
    api.get<ApiResponse<BehavioralPrompt[]>>(`/behavioral-prompts/event/${eventId}`, { params }),
};

// Documents API
export const documentsApi = {
  getAll: (params?: { page?: number; limit?: number; eventId?: string; isProcessed?: boolean; language?: string; fileType?: string }) =>
    api.get<ApiResponse<DocumentInfo[]>>('/documents', { params }),
  
  getById: (id: string) =>
    api.get<ApiResponse<DocumentInfo>>(`/documents/${id}`),
  
  upload: (eventId: string, file: File, language?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventId', eventId);
    if (language) formData.append('language', language);
    
    return api.post<ApiResponse<{ document: DocumentInfo; extractedText: string; summary: string; keywords: string[] }>>('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  delete: (id: string) =>
    api.delete<ApiResponse<void>>(`/documents/${id}`),
  
  reprocess: (id: string) =>
    api.post<ApiResponse<{ document: DocumentInfo; extractedText: string; summary: string; keywords: string[] }>>(`/documents/${id}/reprocess`),
  
  getByEvent: (eventId: string, params?: { page?: number; limit?: number; isProcessed?: boolean }) =>
    api.get<ApiResponse<DocumentInfo[]>>(`/documents/event/${eventId}`, { params }),
  
  getText: (id: string) =>
    api.get<ApiResponse<{ documentId: string; fileName: string; extractedText: string; wordCount: number; language: string }>>(`/documents/${id}/text`),
};

// Sessions API
export const sessionsApi = {
  create: (data: { userId: string; eventId: string }) =>
    api.post<ApiResponse<Session>>('/sessions', data),
  
  getById: (sessionId: string) =>
    api.get<ApiResponse<Session>>(`/sessions/${sessionId}`),
  
  getByUser: (userId: string, params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<Session[]>>(`/sessions/user/${userId}`, { params }),
  
  getByEvent: (
    eventId: string,
    params?: {
      page?: number;
      limit?: number;
      includeInactive?: boolean;
      includeExpired?: boolean;
      search?: string;
      startDate?: string;
      endDate?: string;
    }
  ) =>
    api.get<ApiResponse<Session[]>>(`/sessions/event/${eventId}`, { params }),
  
  getByUserAndEvent: (userId: string, eventId: string, params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<Session[]>>(`/sessions/user/${userId}/event/${eventId}`, { params }),
  
  deactivate: (sessionId: string) =>
    api.put<ApiResponse<void>>(`/sessions/${sessionId}/deactivate`),
  
  extend: (sessionId: string, hours?: number) =>
    api.put<ApiResponse<Session>>(`/sessions/${sessionId}/extend`, { hours }),
  
  getStats: (eventId?: string) =>
    api.get<ApiResponse<SessionStats>>('/sessions/stats', { params: { eventId } }),
  
  exportConversations: (data: { eventId: string; startDate: string; endDate: string; format?: 'csv' | 'json' | 'both' }) =>
    api.post<ApiResponse<{ exportInfo: any; csvData?: string; jsonData?: any }>>('/sessions/export-conversations', data),
  
};

// Chat API
export const chatApi = {
  sendMessage: (data: ChatRequest) =>
    api.post<ApiResponse<ChatResponse>>('/chat', data),
  
  getHistory: (sessionId: string, params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<{ conversations: any[]; sessionInfo: any }>>(`/chat/history/${sessionId}`, { params }),
  
  getSummary: (sessionId: string, params?: { regenerate?: boolean }) =>
    api.get<ApiResponse<{ summary: string; sessionId: string }>>(`/chat/summary/${sessionId}`, { params }),
  
  getCustomAnalysis: (sessionId: string, prompt: string) =>
    api.post<ApiResponse<{ analysis: string; sessionId: string }>>(`/chat/analyze/${sessionId}`, { prompt }),
};

// Health API
export const healthApi = {
  check: () =>
    api.get<ApiResponse<{ uptime: number; timestamp: string }>>('/health'),
};

// Cost Analytics API
export const costApi = {
  getSummary: () =>
    api.get<ApiResponse<any>>('/costs/summary'),
  
  getEventAnalytics: (eventId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get<ApiResponse<any>>(`/costs/analytics/event/${eventId}`, { params }),
  
  getUserAnalytics: (userId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get<ApiResponse<any>>(`/costs/analytics/user/${userId}`, { params }),
  
  getOverallAnalytics: (params?: { startDate?: string; endDate?: string }) =>
    api.get<ApiResponse<any>>('/costs/analytics/overall', { params }),
  
  getHighCostOperations: (params?: { limit?: number }) =>
    api.get<ApiResponse<any>>('/costs/high-cost-operations', { params }),
  
  getCostRecords: (params?: { 
    page?: number; 
    limit?: number; 
    operationType?: string; 
    modelName?: string; 
    startDate?: string; 
    endDate?: string; 
  }) =>
    api.get<ApiResponse<any>>('/costs/operations', { params }),
  
  getModelPricing: () =>
    api.get<ApiResponse<any>>('/costs/pricing'),
};

// Notifications API
export const notificationsApi = {
  createForEvent: (eventId: string, data: { title: string; body: string; priority?: 'info' | 'warning' | 'critical'; expiresAt?: string | null }) =>
    api.post<ApiResponse<Notification>>(`/events/${eventId}/notifications`, data),

  listForEvent: (eventId: string, params?: { since?: string }) =>
    api.get<ApiResponse<{ notifications: Notification[] }>>(`/events/${eventId}/notifications`, { params }),

  markSeen: (notificationId: string, data?: { seenAt?: string; userId?: string }) =>
    api.post<ApiResponse<{ success: boolean }>>(`/notifications/${notificationId}/seen`, data),

  getReceipts: (eventId: string, notificationId: string) =>
    api.get<ApiResponse<{ notificationId: string; seenCount: number; receipts: NotificationReceipt[] }>>(`/events/${eventId}/notifications/${notificationId}/receipts`),
};

// Unified API service for backward compatibility
export const apiService = {
  // Generic methods that return the response data directly
  get: async (url: string, config?: any) => {
    const response = await api.get(url, config);
    return response.data;
  },
  post: async (url: string, data?: any, config?: any) => {
    const response = await api.post(url, data, config);
    return response.data;
  },
  put: async (url: string, data?: any, config?: any) => {
    const response = await api.put(url, data, config);
    return response.data;
  },
  delete: async (url: string, config?: any) => {
    const response = await api.delete(url, config);
    return response.data;
  },
  
  // Convenience methods
  events: eventsApi,
  users: usersApi,
  guardrails: guardrailsApi,
  behavioralPrompts: behavioralPromptsApi,
  documents: documentsApi,
  rag: {
    createForDocument: (id: string, data?: import('../types').CreateRAGRequest) =>
      api.post<ApiResponse<any>>(`/documents/${id}/rag/create`, data || {}),
    createRAGBatch: (payload: { eventId: string; documentIds?: string[]; mode?: 'all_missing'; chunkSize?: number; chunkOverlap?: number; strategy?: string }) =>
      api.post<ApiResponse<{ results: any[]; created: number; skipped_already_exists: number; skipped_not_processed: number; failed: number }>>('/rag/batch', payload),
    getRAGDetails: (id: string) =>
      api.get<ApiResponse<import('../types').DocumentRAGInfo>>(`/documents/${id}/rag`),
    getChunks: (id: string, params?: { page?: number; limit?: number }) =>
      api.get<ApiResponse<{ chunks: import('../types').RAGChunk[]; ragInfo: import('../types').DocumentRAGInfo }>>(`/documents/${id}/rag/chunks`, { params }),
    deleteRAG: (id: string) =>
      api.delete<ApiResponse<void>>(`/documents/${id}/rag`),
    search: (payload: { query: string; eventId: string; limit?: number }) =>
      api.post<ApiResponse<{ results: any[] }>>('/rag/search', payload),
  },
  sessions: sessionsApi,
  chat: chatApi,
  health: healthApi,
  costs: costApi,
  notifications: notificationsApi,
};

// Feedback API
export interface FeedbackListParams {
  page?: number;
  limit?: number;
  status?: string;
  category?: string;
  startDate?: string;
  endDate?: string;
  sentiment?: string;
  assigneeUserId?: string;
  eventId?: string;
  clientId?: string;
}

export interface FeedbackStats {
  total: number;
  thumbsUp: number;
  thumbsDown: number;
  thumbsUpPercentage: number;
  thumbsDownPercentage: number;
}

export const feedbackApi = {
  create: (payload: any) => api.post<ApiResponse<{ feedbackId: string; status: string; createdAt: string }>>('/feedback', payload),
  list: (params?: FeedbackListParams) => api.get<ApiResponse<import('../types').FeedbackItem[]>>('/feedback', { params }),
  getById: (feedbackId: string) => api.get<ApiResponse<import('../types').FeedbackItem>>(`/feedback/${feedbackId}`),
  update: (feedbackId: string, patch: any) => api.patch<ApiResponse<import('../types').FeedbackItem>>(`/feedback/${feedbackId}`, patch),
  remove: (feedbackId: string) => api.delete<ApiResponse<void>>(`/feedback/${feedbackId}`),
  getStats: (params?: { eventId?: string; clientId?: string }) =>
    api.get<ApiResponse<FeedbackStats>>('/feedback/stats', { params }),
  getBadgeCounts: () =>
    api.get<ApiResponse<{ feedbackOpen: number; callbackOpen: number }>>('/feedback/badge-counts'),
};

// Dashboard API
export interface DashboardEventSummary {
  id: string;
  name: string;
  status: 'active' | 'inactive';
  totalUsers: number;
  newUsersToday: number;
  costToday: number;
  totalCost: number;
  totalRequests: number;
  requestsToday: number;
  thumbsUpPercentage?: number;
  thumbsDownPercentage?: number;
  totalConversations: number;
  conversationsToday: number;
}

export interface DashboardSummary {
  health: { uptime: number };
  aggregate: {
    totalUsers: number;
    newUsersToday: number;
    totalCost: number;
    costToday: number;
    totalRequests: number;
    requestsToday: number;
    totalConversations: number;
    conversationsToday: number;
  };
  events: DashboardEventSummary[];
}

export const dashboardApi = {
  getSummary: (params?: { clientId?: string }) =>
    api.get<ApiResponse<DashboardSummary>>('/dashboard/summary', { params }),
};

// Callback Requests API
export interface CallbackRequestListParams {
  page?: number;
  limit?: number;
  status?: string;
  eventId?: string;
  clientId?: string; // admin-only filter
}

export const callbackRequestsApi = {
  create: (payload: {
    eventId: string;
    userId: string;
    sessionId?: string;
    phone: string;
    issue: string;
    expectedResolution: string;
  }) => api.post<ApiResponse<{ callbackRequestId: string; status: string; createdAt: string }>>('/callback-requests', payload),
  list: (params?: CallbackRequestListParams) => api.get<ApiResponse<import('../types').CallbackRequestItem[]>>('/callback-requests', { params }),
  updateStatus: (callbackRequestId: string, status: 'new' | 'in_progress' | 'resolved' | 'cancelled') =>
    api.patch<ApiResponse<import('../types').CallbackRequestItem>>(`/callback-requests/${callbackRequestId}/status`, { status }),
};

// Sentiment Analysis API
export interface SentimentAnalysisGenerateRequest {
  eventId: string;
  generatedBy: string;
  startDate?: string;
  endDate?: string;
}

export interface SentimentAnalysisListParams {
  eventId?: string;
  status?: 'generating' | 'completed' | 'failed';
  page?: number;
  limit?: number;
}

export const sentimentAnalysisApi = {
  generate: (data: SentimentAnalysisGenerateRequest) => 
    api.post<ApiResponse<{ reportId: string; status: string; message: string }>>('/sentiment-analysis/generate', data),
  
  list: (params?: SentimentAnalysisListParams) => 
    api.get<ApiResponse<import('../types').SentimentAnalysisReport[]>>('/sentiment-analysis', { params }),
  
  getById: (reportId: string) => 
    api.get<ApiResponse<import('../types').SentimentAnalysisReport>>(`/sentiment-analysis/${reportId}`),
  
  delete: (reportId: string) => 
    api.delete<ApiResponse<{ success: boolean; message: string }>>(`/sentiment-analysis/${reportId}`),
  
  download: (reportId: string, format: 'markdown' | 'pdf' | 'docx') => {
    const url = `/sentiment-analysis/${reportId}/download?format=${format}`;
    return api.get(url, { responseType: 'blob' });
  },
};

// Authentication API
export const authApi = {
  login: (data: LoginRequest) =>
    api.post<ApiResponse<AuthResponse>>('/auth/login', data),
  
  register: (data: RegisterRequest) =>
    api.post<ApiResponse<AuthResponse>>('/auth/register', data),
  
  getMe: () =>
    api.get<ApiResponse<AppUser>>('/auth/me'),
  
  changePassword: (data: ChangePasswordRequest) =>
    api.post<ApiResponse<void>>('/auth/change-password', data),
};

// Admin User Management API
export const adminApi = {
  getAllUsers: (params?: { 
    page?: number; 
    limit?: number; 
    role?: 'admin' | 'client'; 
    isActive?: boolean; 
    search?: string;
  }) =>
    api.get<ApiResponse<{ users: AppUser[]; pagination: any }>>('/admin/users', { params }),
  
  getUserById: (id: string) =>
    api.get<ApiResponse<AppUser>>(`/admin/users/${id}`),
  
  createCompanyUser: (data: CreateCompanyUserRequest) =>
    api.post<ApiResponse<AppUser>>('/admin/users', data),
  
  updateUser: (id: string, data: UpdateAppUserRequest) =>
    api.put<ApiResponse<AppUser>>(`/admin/users/${id}`, data),
  
  deleteUser: (id: string) =>
    api.delete<ApiResponse<void>>(`/admin/users/${id}`),
  
  resetUserPassword: (id: string, data: ResetPasswordRequest) =>
    api.post<ApiResponse<void>>(`/admin/users/${id}/reset-password`, data),
  
  toggleUserStatus: (id: string) =>
    api.patch<ApiResponse<AppUser>>(`/admin/users/${id}/toggle-status`),

  getSignupForm: (userId: string) =>
    api.get<ApiResponse<{ signupFormFields: import('../types').SignupFormField[] }>>(`/admin/users/${userId}/signup-form`),

  updateSignupForm: (userId: string, data: { signupFormFields: SignupFormField[] }) =>
    api.put<ApiResponse<AppUser>>(`/admin/users/${userId}/signup-form`, data),
  
  getStats: () =>
    api.get<ApiResponse<UserStatsResponse>>('/admin/stats'),

  listAITraces: (params?: {
    page?: number;
    limit?: number;
    traceId?: string;
    parentTraceId?: string;
    operation?: string;
    success?: boolean;
    provider?: string;
    model?: string;
    userId?: string;
    sessionId?: string;
    messageId?: string;
    documentId?: string;
    eventId?: string;
    clientId?: string;
    ragSessionId?: string;
    from?: string;
    to?: string;
    answered?: boolean;
    refusalReason?: string;
    resolutionStatus?: string;
  }) =>
    api.get<ApiResponse<{ traces: AITraceItem[]; pagination: any }>>('/admin/ai-traces', { params }),

  getAITrace: (traceId: string) =>
    api.get<ApiResponse<AITraceDetailResponse>>(`/admin/ai-traces/${traceId}`),

  updateAITraceResolution: (traceId: string, resolutionStatus: 'resolved' | 'not_resolved') =>
    api.patch<ApiResponse<AITraceItem>>(`/admin/ai-traces/${traceId}`, { resolutionStatus }),

  // Prompt templates
  getPromptRegistry: () =>
    api.get<ApiResponse<{ registry: any[] }>>('/admin/prompts/registry'),
  syncPrompts: () =>
    api.post<ApiResponse<{ created: number; total: number }>>('/admin/prompts/sync'),
  listPrompts: (params?: { page?: number; limit?: number; key?: string; scopeType?: string; scopeId?: string }) =>
    api.get<ApiResponse<{ prompts: any[]; pagination: any }>>('/admin/prompts', { params }),
  getPrompt: (id: string) =>
    api.get<ApiResponse<any>>(`/admin/prompts/${id}`),
  createPrompt: (data: any) =>
    api.post<ApiResponse<any>>('/admin/prompts', data),
  updatePrompt: (id: string, data: { template?: string; saveAsNew?: boolean; name?: string; description?: string; variables?: string[]; isActive?: boolean }) =>
    api.put<ApiResponse<any>>(`/admin/prompts/${id}`, data),
  restorePromptVersion: (id: string, versionNumber: number) =>
    api.post<ApiResponse<any>>(`/admin/prompts/${id}/restore`, { versionNumber }),
  deletePrompt: (id: string) =>
    api.delete<ApiResponse<void>>(`/admin/prompts/${id}`),

  // AI models
  listAIModels: (params?: { page?: number; limit?: number; provider?: string; isActive?: boolean }) =>
    api.get<ApiResponse<{ models: any[]; pagination: any }>>('/admin/ai-models', { params }),
  getAIModel: (id: string) =>
    api.get<ApiResponse<any>>(`/admin/ai-models/${id}`),
  createAIModel: (data: any) =>
    api.post<ApiResponse<any>>('/admin/ai-models', data),
  updateAIModel: (id: string, data: any) =>
    api.put<ApiResponse<any>>(`/admin/ai-models/${id}`, data),
  deleteAIModel: (id: string) =>
    api.delete<ApiResponse<void>>(`/admin/ai-models/${id}`),

  // AI module mappings
  listAIModuleMappings: (params?: { page?: number; limit?: number; module?: string; scopeType?: string; scopeId?: string }) =>
    api.get<ApiResponse<{ mappings: any[]; pagination: any }>>('/admin/ai-module-mappings', { params }),
  getAIModuleMapping: (id: string) =>
    api.get<ApiResponse<any>>(`/admin/ai-module-mappings/${id}`),
  createAIModuleMapping: (data: any) =>
    api.post<ApiResponse<any>>('/admin/ai-module-mappings', data),
  updateAIModuleMapping: (id: string, data: any) =>
    api.put<ApiResponse<any>>(`/admin/ai-module-mappings/${id}`, data),
  deleteAIModuleMapping: (id: string) =>
    api.delete<ApiResponse<void>>(`/admin/ai-module-mappings/${id}`),
};

// Client Allowlist API (client role only - event-scoped, supports email and phone tabs)
export const clientAllowlistApi = {
  getSettings: (eventId: string) =>
    api.get<ApiResponse<{
      useEmailAllowlist: boolean;
      usePhoneAllowlist: boolean;
      emailCount: number;
      phoneCount: number;
    }>>('/client/allowlist/settings', { params: { eventId } }),

  updateSettings: (eventId: string, data: { useEmailAllowlist?: boolean; usePhoneAllowlist?: boolean }) =>
    api.put<ApiResponse<{ useEmailAllowlist: boolean; usePhoneAllowlist: boolean }>>('/client/allowlist/settings', { eventId, ...data }),

  uploadCsv: (eventId: string, type: 'email' | 'phone', file: File, action: 'replace' | 'append') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventId', eventId);
    formData.append('type', type);
    formData.append('action', action);
    return api.post<ApiResponse<{
      uploaded: number;
      skippedDuplicates: number;
      invalid: string[];
      totalCount: number;
    }>>('/client/allowlist/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getList: (eventId: string, type: 'email' | 'phone', params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<{ values: string[]; pagination: { page: number; limit: number; total: number } }>>('/client/allowlist', {
      params: { eventId, type, ...params },
    }),

  addEntry: (eventId: string, type: 'email' | 'phone', value: string) =>
    api.post<ApiResponse<{ value: string; totalCount: number }>>('/client/allowlist/entry', { eventId, type, value }),

  deleteEntries: (eventId: string, type: 'email' | 'phone', values: string[]) =>
    api.delete<ApiResponse<{ deleted: number; totalCount: number }>>('/client/allowlist/entries', {
      data: { eventId, type, values },
    }),

  clearList: (eventId: string, type: 'email' | 'phone') =>
    api.delete<ApiResponse<{ deleted: number }>>('/client/allowlist/all', {
      params: { eventId, type },
    }),
};

// Settings API
export const settingsApi = {
  getSettings: () =>
    api.get<ApiResponse<{
      _id: string;
      name: string;
      email: string;
      role: 'admin' | 'client';
      phone: string;
      isActive: boolean;
      hasOpenaiApiKey: boolean;
      hasGeminiApiKey: boolean;
      selfImprovementEnabled: boolean;
      createdAt: string;
      updatedAt: string;
    }>>('/settings'),
  
  updatePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put<ApiResponse<null>>('/settings/password', data),
  
  updateApiKeys: (data: { openaiApiKey?: string; geminiApiKey?: string }) =>
    api.put<ApiResponse<{ hasOpenaiApiKey: boolean; hasGeminiApiKey: boolean }>>('/settings/api-keys', data),

  updateGeminiApiKey: (geminiApiKey: string) =>
    api.put<ApiResponse<{ hasGeminiApiKey: boolean }>>('/settings/api-keys/gemini', { geminiApiKey }),

  updateOpenaiApiKey: (openaiApiKey: string) =>
    api.put<ApiResponse<{ hasOpenaiApiKey: boolean }>>('/settings/api-keys/openai', { openaiApiKey }),

  getApiKeysStatus: () =>
    api.get<ApiResponse<{ hasOpenaiApiKey: boolean; hasGeminiApiKey: boolean }>>('/settings/api-keys/status'),
};

// Client-scoped Self-Improvement API (for KA admins with selfImprovementEnabled)
export const clientSelfImprovementApi = {
  listTraces: (params?: {
    page?: number;
    limit?: number;
    answered?: boolean;
    refusalReason?: string;
    resolutionStatus?: string;
    messageId?: string;
    eventId?: string;
  }) =>
    api.get<ApiResponse<{ traces: AITraceItem[]; pagination: any }>>('/settings/self-improvement/traces', { params }),

  getTrace: (traceId: string) =>
    api.get<ApiResponse<AITraceDetailResponse>>(`/settings/self-improvement/traces/${traceId}`),

  updateTraceResolution: (traceId: string, resolutionStatus: 'resolved' | 'not_resolved') =>
    api.patch<ApiResponse<AITraceItem>>(`/settings/self-improvement/traces/${traceId}`, { resolutionStatus }),
};

// Data Ingestion API
export const dataIngestionApi = {
  preflight: (file: File, eventId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventId', eventId);
    return api.post<ApiResponse<{
      sheets: Array<{
        sheetName: string;
        headers: string[];
        rowCount: number;
        detectedTypes: Record<string, string>;
        existingSchema: { name: string; slug: string; recordCount: number } | null;
        headersMismatch: boolean;
      }>;
    }>>('/data-ingest/preflight', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  ingest: (
    file: File,
    eventId: string,
    sheetName: string,
    mode: 'replace' | 'incremental',
    onProgress?: (pct: number) => void,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventId', eventId);
    formData.append('sheetName', sheetName);
    formData.append('mode', mode);
    return api.post<ApiResponse<{
      inserted: number;
      skipped: number;
      total: number;
      schemaName: string;
      schemaSlug: string;
      mode: string;
    }>>('/data-ingest', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            onProgress(pct);
          }
        : undefined,
    });
  },
};

// Team Members API (client role only)
export const teamMembersApi = {
  list: () =>
    api.get<ApiResponse<import('../types').TeamMember[]>>('/team-members'),
  create: (data: { name: string; email: string; password?: string }) =>
    api.post<ApiResponse<import('../types').TeamMember>>('/team-members', data),
  setPassword: (id: string, password: string) =>
    api.patch<ApiResponse<null>>(`/team-members/${id}/password`, { password }),
  remove: (id: string) =>
    api.delete<ApiResponse<null>>(`/team-members/${id}`),
};

export default api;
